import React, { useState, useEffect } from 'react';
import {
    Search, Mail, Calendar, CheckSquare, StickyNote,
    FolderOpen, Shield, ChevronRight, Plug, AlertTriangle,
    ArrowLeft, MessageSquare, Code, Cloud, Users, Zap,
} from 'lucide-react';
import { api } from '../lib/api';
import { useReplica } from '../contexts/ReplicaContext';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { replicaService } from '../lib/services';

// ============================================================================
// Types
// ============================================================================
interface MCPTool {
    id: string;
    name: string;
    description: string;
    category: string;
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    requiresApproval: boolean;
    trustTier: 'first-party' | 'enterprise-approved' | 'third-party';
    readiness: 'available' | 'needs-credentials' | 'disabled' | 'restricted';
    permissions: string[];
    connectionType?: 'oauth2' | 'api_key' | 'direct' | 'webhook';
    connection?: {
        status: string;
        connectedAt?: string;
        scopes?: string[];
        providerEmail?: string;
        errorMessage?: string;
    } | null;
}

interface ConnectorSetupState {
    tool: MCPTool | null;
    step: number;
    selectedScopes: string[];
    selectedReplicaId: string;
    approvalPolicy: 'ask-first' | 'auto-within-policy' | 'never';
}

interface SimpleReplica {
    id: string;
    name: string;
    category?: string;
}

// ============================================================================
// Constants
// ============================================================================
const CATEGORY_ICONS: Record<string, React.ElementType> = {
    'Email': Mail,
    'Calendar': Calendar,
    'Tasks': CheckSquare,
    'Notes': StickyNote,
    'Files': FolderOpen,
    'Communication': MessageSquare,
    'Development': Code,
    'Cloud Storage': Cloud,
    'CRM': Users,
    'Productivity': Zap,
    'Social Media': Users,
    'Other': Plug,
};

// Per-tool brand logos (inline SVGs with accurate brand colors)
const TOOL_LOGOS: Record<string, { bg: string; svg: React.ReactNode }> = {
    'gmail': {
        bg: 'bg-red-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><path d="M2 6l10 7 10-7" stroke="#EA4335" strokeWidth="2" strokeLinecap="round" /><rect x="2" y="5" width="20" height="14" rx="2" stroke="#EA4335" strokeWidth="1.5" fill="none" /></svg>,
    },
    'outlook': {
        bg: 'bg-hig-accent/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><rect x="2" y="4" width="20" height="16" rx="2" stroke="#0078D4" strokeWidth="1.5" /><path d="M2 8l10 5 10-5" stroke="#0078D4" strokeWidth="1.5" /></svg>,
    },
    'google-calendar': {
        bg: 'bg-hig-accent/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke="#4285F4" strokeWidth="1.5" /><line x1="3" y1="9" x2="21" y2="9" stroke="#4285F4" strokeWidth="1.5" /><line x1="8" y1="4" x2="8" y2="7" stroke="#4285F4" strokeWidth="1.5" /><line x1="16" y1="4" x2="16" y2="7" stroke="#4285F4" strokeWidth="1.5" /><text x="12" y="17" textAnchor="middle" fill="#4285F4" fontSize="7" fontWeight="bold">31</text></svg>,
    },
    'slack': {
        bg: 'bg-purple-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4"><path d="M6 15a2 2 0 01-2-2 2 2 0 012-2h2v2a2 2 0 01-2 2zm5-2a2 2 0 01-2-2V6a2 2 0 114 0v5a2 2 0 01-2 2z" fill="#E01E5A" /><path d="M18 11a2 2 0 012 2 2 2 0 01-2 2h-2v-2a2 2 0 012-2zm-5 2a2 2 0 012 2v5a2 2 0 11-4 0v-5a2 2 0 012-2z" fill="#36C5F0" /><path d="M11 6a2 2 0 01-2-2 2 2 0 012-2h0a2 2 0 012 2v2h-2zm2 5a2 2 0 012-2h5a2 2 0 110 4h-5a2 2 0 01-2-2z" fill="#2EB67D" /><path d="M15 18a2 2 0 012 2 2 2 0 01-2 2 2 2 0 01-2-2v-2h2zm-2-5a2 2 0 01-2 2H6a2 2 0 110-4h5a2 2 0 012 2z" fill="#ECB22E" /></svg>,
    },
    'whatsapp': {
        bg: 'bg-green-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#25D366"><path d="M12 2C6.48 2 2 6.48 2 12c0 1.77.47 3.44 1.28 4.88L2 22l5.22-1.25A9.94 9.94 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm5.2 14.16c-.22.62-1.28 1.18-1.78 1.26-.46.06-1.04.1-1.68-.1a15 15 0 01-2.52-1.12c-2.18-1.24-3.6-3.48-3.72-3.64-.1-.16-.88-1.18-.88-2.24s.56-1.58.76-1.8c.2-.22.44-.28.58-.28h.42c.14 0 .32-.04.5.38.18.42.64 1.56.7 1.68.06.12.1.26.02.42-.08.16-.12.26-.24.4l-.36.42c-.12.12-.24.26-.1.5.14.24.6 1 1.3 1.62.9.8 1.64 1.04 1.88 1.16.24.12.38.1.52-.06.14-.16.58-.68.74-.92.16-.22.32-.18.52-.1.22.06 1.36.64 1.58.76.22.12.38.18.44.28.06.1.06.56-.16 1.18z" /></svg>,
    },
    'discord': {
        bg: 'bg-indigo-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#5865F2"><path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 00-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 00-4.8 0c-.14-.34-.35-.76-.54-1.09-.01-.02-.04-.03-.07-.03-1.5.26-2.93.71-4.27 1.33-.01 0-.02.01-.03.02-2.72 4.07-3.47 8.03-3.1 11.95 0 .02.01.04.03.05 1.8 1.32 3.53 2.12 5.24 2.65.03.01.06 0 .07-.02.4-.55.76-1.13 1.07-1.74.02-.04 0-.08-.04-.09-.57-.22-1.11-.48-1.64-.78-.04-.02-.04-.08-.01-.11.11-.08.22-.17.33-.25.02-.02.04-.02.07-.01 3.44 1.57 7.15 1.57 10.55 0 .02-.01.05-.01.07.01.11.09.22.17.33.26.04.03.04.09-.01.11-.52.31-1.07.56-1.64.78-.04.01-.05.06-.04.09.32.61.68 1.19 1.07 1.74.02.03.05.03.07.02 1.72-.53 3.45-1.33 5.24-2.65.02-.01.03-.03.03-.05.44-4.53-.73-8.46-3.1-11.95-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12 0-1.17.84-2.12 1.89-2.12 1.06 0 1.9.96 1.89 2.12 0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12 0-1.17.84-2.12 1.89-2.12 1.06 0 1.9.96 1.89 2.12 0 1.17-.83 2.12-1.89 2.12z" /></svg>,
    },
    'zoom': {
        bg: 'bg-hig-accent/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><rect x="2" y="5" width="15" height="14" rx="3" stroke="#2D8CFF" strokeWidth="1.5" /><path d="M17 10l4-3v10l-4-3v-4z" fill="#2D8CFF" /></svg>,
    },
    'github': {
        bg: 'bg-gray-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0012 2z" /></svg>,
    },
    'gitlab': {
        bg: 'bg-orange-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FC6D26"><path d="M12 21.35l3.36-10.35H8.64L12 21.35z" /><path d="M8.64 11h6.72L12 2.45 8.64 11z" opacity="0.7" /><path d="M3.18 11h5.46L5.82 2.45 3.18 11z" /><path d="M3.18 11l-1 3.09a.68.68 0 00.25.76L12 21.35l-8.82-10.35z" opacity="0.7" /><path d="M15.36 11h5.46L18.18 2.45 15.36 11z" /><path d="M20.82 11l1 3.09a.68.68 0 01-.25.76L12 21.35l8.82-10.35z" opacity="0.7" /></svg>,
    },
    'jira': {
        bg: 'bg-hig-accent/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0052CC"><path d="M12.005 2L5.235 8.77l3.18 3.18L12 8.36l3.585 3.59 3.18-3.18L12.005 2z" /><path d="M8.415 11.95l-3.18 3.18L12.005 22l6.77-6.87-3.18-3.18-3.59 3.59-3.59-3.59z" opacity="0.7" /></svg>,
    },
    'google-drive': {
        bg: 'bg-yellow-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><path d="M8 2l8 0 4 7h-8l-4-7z" fill="#FBBC04" /><path d="M2.5 15L8 2l4 7-5.5 9.5L2.5 15z" fill="#34A853" /><path d="M6.5 18.5L20 9l-4 7H6.5z" fill="#4285F4" /></svg>,
    },
    'dropbox': {
        bg: 'bg-hig-accent/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0061FF"><path d="M7 3l5 3.5L7 10l-5-3.5L7 3zm10 0l5 3.5-5 3.5-5-3.5L17 3zM2 13.5L7 10l5 3.5-5 3.5-5-3.5zm15-3.5l5 3.5-5 3.5-5-3.5L17 10z" /><path d="M7 17.5l5-3.5 5 3.5-5 3.5-5-3.5z" opacity="0.6" /></svg>,
    },
    'onedrive': {
        bg: 'bg-hig-accent/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><path d="M10 8c2.5-2 6-1.5 7.5.5 1.5-.5 3.5.5 3.5 2.5 0 2-1.5 3-3.5 3H6c-2.5 0-4-2-4-4 0-2 1.5-3.5 3.5-3.5C6.5 5 8 5.5 10 8z" fill="#0078D4" /></svg>,
    },
    'salesforce': {
        bg: 'bg-hig-accent/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><path d="M10 5.5c1-1.5 2.8-2.5 4.8-2.5 2.1 0 3.9 1.1 4.9 2.8.8-.3 1.6-.5 2.5-.5C24.8 5.3 27 7.8 27 10.8" stroke="#00A1E0" strokeWidth="0" /><ellipse cx="12" cy="14" rx="10" ry="7" fill="#00A1E0" /><text x="12" y="16" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">SF</text></svg>,
    },
    'hubspot': {
        bg: 'bg-orange-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FF7A59"><circle cx="12" cy="12" r="3" /><circle cx="18" cy="6" r="2" /><circle cx="18" cy="18" r="2" /><circle cx="6" cy="12" r="2" /><line x1="14.5" y1="10" x2="16.5" y2="7.5" stroke="#FF7A59" strokeWidth="1.5" /><line x1="14.5" y1="14" x2="16.5" y2="16.5" stroke="#FF7A59" strokeWidth="1.5" /><line x1="9" y1="12" x2="6" y2="12" stroke="#FF7A59" strokeWidth="0" /><line x1="8" y1="12" x2="9.5" y2="12" stroke="#FF7A59" strokeWidth="1.5" /></svg>,
    },
    'notion': {
        bg: 'bg-gray-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M4.5 3.5h11l4 4v13h-15V3.5z" stroke="currentColor" strokeWidth="1.5" fill="none" /><path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>,
    },
    'trello': {
        bg: 'bg-hig-accent/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="#0079BF" strokeWidth="1.5" /><rect x="6" y="6" width="4" height="10" rx="1" fill="#0079BF" /><rect x="13" y="6" width="4" height="6" rx="1" fill="#0079BF" /></svg>,
    },
    'asana': {
        bg: 'bg-rose-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#F06A6A"><circle cx="12" cy="8" r="3.5" /><circle cx="6" cy="16" r="3.5" /><circle cx="18" cy="16" r="3.5" /></svg>,
    },
    // Social Media
    'twitter-x': {
        bg: 'bg-gray-500/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>,
    },
    'linkedin-feed': {
        bg: 'bg-[#0A66C2]/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>,
    },
    'instagram-feed': {
        bg: 'bg-[#E4405F]/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#E4405F"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>,
    },
    'reddit-feed': {
        bg: 'bg-[#FF4500]/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FF4500"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" /></svg>,
    },
    'rss-feeds': {
        bg: 'bg-[#F26522]/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#F26522"><circle cx="6.18" cy="17.82" r="2.18" /><path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z" /></svg>,
    },
};

const RISK_COLORS: Record<string, string> = {
    low: 'text-emerald-400 bg-emerald-500/10',
    medium: 'text-amber-400 bg-amber-500/10',
    high: 'text-orange-400 bg-orange-500/10',
    critical: 'text-red-400 bg-red-500/10',
};

const TRUST_BADGES: Record<string, { label: string; color: string }> = {
    'first-party': { label: 'First-party', color: 'text-hig-accent bg-hig-accent/10 border-hig-accent/20' },
    'enterprise-approved': { label: 'Enterprise', color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
    'third-party': { label: 'Third-party', color: 'text-gray-400 bg-gray-500/10 border-gray-500/20' },
};

// ============================================================================
// Component
// ============================================================================
interface IntegrationsCatalogViewProps {
    onNavigate?: (view: string) => void;
    hideHeader?: boolean;
}

export default function IntegrationsCatalogView({ onNavigate, hideHeader }: IntegrationsCatalogViewProps) {
    const [tools, setTools] = useState<MCPTool[]>([]);
    const [grouped, setGrouped] = useState<Record<string, MCPTool[]>>({});
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [setupState, setSetupState] = useState<ConnectorSetupState>({
        tool: null, step: 0, selectedScopes: [], selectedReplicaId: '', approvalPolicy: 'ask-first',
    });
    const [connectingToolId, setConnectingToolId] = useState<string | null>(null);
    const [connectedTools, setConnectedTools] = useState<Set<string>>(new Set());
    const [connectError, setConnectError] = useState<string | null>(null);
    const [oauthCallbackError, setOauthCallbackError] = useState<string | null>(null);
    const { userReplicas: contextReplicas } = useReplica();
    const { user } = useAuth();
    const { canHire } = useWorkspace();

    // Fetch replicas from backend API (source of truth) with fallback to context
    const [backendReplicas, setBackendReplicas] = useState<SimpleReplica[]>([]);

    useEffect(() => {
        if (!user?.uid) return;
        replicaService.getUserReplicas(user.uid).then(res => {
            if (res.data?.replicas && res.data.replicas.length > 0) {
                setBackendReplicas(res.data.replicas.map((r: any) => ({
                    id: r.id || '',
                    name: r.name || 'Unnamed',
                    category: r.category || '',
                })));
            }
        }).catch(err => {
            console.warn('Failed to fetch replicas from backend:', err);
        });
    }, [user?.uid]);

    // Merge: backend replicas take priority, then add context-only replicas
    const contextMapped: SimpleReplica[] = (contextReplicas || []).map((r: any) => ({
        id: r.id || '',
        name: r.name || 'Unnamed',
        category: r.category || '',
    }));
    const backendIds = new Set(backendReplicas.map(r => r.id));
    const replicas: SimpleReplica[] = [
        ...backendReplicas,
        ...contextMapped.filter(r => !backendIds.has(r.id)),
    ];

    const isEnterprise = canHire;

    useEffect(() => {
        loadTools();
        // Check URL params for OAuth callback results
        const params = new URLSearchParams(window.location.search);
        const oauthStatus = params.get('status');
        const oauthTool = params.get('tool');
        if (oauthStatus && oauthTool) {
            if (oauthStatus === 'success') {
                setConnectedTools(prev => new Set([...prev, oauthTool]));
            } else if (oauthStatus === 'error') {
                const msg = params.get('message');
                setOauthCallbackError(msg ? decodeURIComponent(msg) : `Failed to connect ${oauthTool}. Please try again.`);
            }
            // Clean up URL params
            const url = new URL(window.location.href);
            url.searchParams.delete('status');
            url.searchParams.delete('tool');
            url.searchParams.delete('message');
            url.searchParams.delete('view');
            window.history.replaceState({}, '', url.toString());
        }
    }, []);

    const loadTools = async () => {
        setLoading(true);
        try {
            // Pass replicaId if available so backend returns connection status
            const replicaId = replicas.length > 0 ? replicas[0].id : undefined;
            const endpoint = replicaId
                ? `/mcp/catalog/tools?replicaId=${replicaId}`
                : '/mcp/catalog/tools';

            // Race the API call against a 6s timeout — fall back to built-in catalog if backend is slow
            const timeoutPromise = new Promise<null>(resolve => setTimeout(() => resolve(null), 6000));
            const response: any = await Promise.race([api.get(endpoint), timeoutPromise]);
            if (response?.data && !response.error) {
                const fetchedTools = response.data.tools || [];
                setTools(fetchedTools);
                setGrouped(response.data.grouped || {});

                // Populate connectedTools from backend response
                const connected = new Set<string>();
                fetchedTools.forEach((t: MCPTool) => {
                    if (t.connection?.status === 'connected') {
                        connected.add(t.id);
                    }
                });
                setConnectedTools(prev => new Set([...prev, ...connected]));

                setLoading(false);
                return;
            }
        } catch (_err) {
            // Fall through to fallback
        }

        // Fallback: build catalog from known tools (backend unavailable)
        const fallbackTools: MCPTool[] = [
            // Email
            { id: 'gmail', name: 'Gmail', description: 'Read, draft, and send emails through your Gmail account with AI assistance.', category: 'Email', riskLevel: 'medium', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write', 'send'] },
            { id: 'outlook', name: 'Outlook', description: 'Manage Microsoft Outlook emails, drafts, and calendar invites.', category: 'Email', riskLevel: 'medium', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write', 'send'] },
            // Calendar
            { id: 'google-calendar', name: 'Google Calendar', description: 'View availability, schedule meetings, and manage Google Calendar events.', category: 'Calendar', riskLevel: 'medium', requiresApproval: true, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            // Communication
            { id: 'slack', name: 'Slack', description: 'Send messages, read channels, and manage Slack workspace interactions.', category: 'Communication', riskLevel: 'medium', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'whatsapp', name: 'WhatsApp', description: 'Send and receive WhatsApp messages via Twilio Business API integration.', category: 'Communication', riskLevel: 'high', requiresApproval: true, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write', 'send'] },
            { id: 'discord', name: 'Discord', description: 'Manage Discord server messages, channels, and bot interactions.', category: 'Communication', riskLevel: 'medium', requiresApproval: false, trustTier: 'third-party', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'zoom', name: 'Zoom', description: 'Schedule, join, and manage Zoom meetings and webinars.', category: 'Communication', riskLevel: 'medium', requiresApproval: true, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            // Development
            { id: 'github', name: 'GitHub', description: 'Manage repositories, pull requests, issues, and GitHub Actions workflows.', category: 'Development', riskLevel: 'medium', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'gitlab', name: 'GitLab', description: 'Manage GitLab projects, merge requests, CI/CD pipelines, and issues.', category: 'Development', riskLevel: 'medium', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'jira', name: 'Jira', description: 'Create, update, and track Jira issues, sprints, and project boards.', category: 'Development', riskLevel: 'medium', requiresApproval: true, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            // Cloud Storage
            { id: 'google-drive', name: 'Google Drive', description: 'Browse, read, and organize files in Google Drive. Share documents securely.', category: 'Cloud Storage', riskLevel: 'low', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'dropbox', name: 'Dropbox', description: 'Access, upload, and manage files in your Dropbox cloud storage.', category: 'Cloud Storage', riskLevel: 'low', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'onedrive', name: 'OneDrive', description: 'Access and manage files stored in Microsoft OneDrive and SharePoint.', category: 'Cloud Storage', riskLevel: 'low', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            // CRM
            { id: 'salesforce', name: 'Salesforce', description: 'Manage leads, contacts, opportunities, and reports in Salesforce CRM.', category: 'CRM', riskLevel: 'high', requiresApproval: true, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'hubspot', name: 'HubSpot', description: 'Manage contacts, deals, marketing campaigns, and HubSpot CRM workflows.', category: 'CRM', riskLevel: 'medium', requiresApproval: true, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            // Productivity
            { id: 'notion', name: 'Notion', description: 'Create pages, manage databases, and organize your Notion workspace.', category: 'Productivity', riskLevel: 'low', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'trello', name: 'Trello', description: 'Manage Trello boards, lists, and cards for project organization.', category: 'Productivity', riskLevel: 'low', requiresApproval: false, trustTier: 'third-party', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'asana', name: 'Asana', description: 'Create and track tasks, projects, and team workflows in Asana.', category: 'Productivity', riskLevel: 'low', requiresApproval: false, trustTier: 'enterprise-approved', readiness: 'available', permissions: ['read', 'write'] },
            // Social Media
            { id: 'twitter-x', name: 'Twitter / X', description: 'Read your timeline, mentions, and trending topics. Your Replica synthesizes tweets into a distilled digest.', category: 'Social Media', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read'], connectionType: 'oauth2' },
            { id: 'linkedin-feed', name: 'LinkedIn', description: 'Read your professional feed, job alerts, and network updates. AI-filtered for relevance.', category: 'Social Media', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read'], connectionType: 'oauth2' },
            { id: 'instagram-feed', name: 'Instagram', description: 'Read your feed and stories summaries. Your Replica extracts key visual and textual content.', category: 'Social Media', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read'], connectionType: 'oauth2' },
            { id: 'reddit-feed', name: 'Reddit', description: 'Read your subscribed subreddits and trending threads. AI highlights discussions worth your attention.', category: 'Social Media', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read'], connectionType: 'oauth2' },
            { id: 'rss-feeds', name: 'RSS Feeds', description: 'Subscribe to custom news sources and blogs. Your Replica summarizes articles into bite-sized insights.', category: 'Social Media', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read'], connectionType: 'direct' },
            // Tasks
            { id: 'tasks.read', name: 'Tasks Read', description: 'View and list tasks across connected task management tools.', category: 'Tasks', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read'] },
            { id: 'tasks.create', name: 'Tasks Create', description: 'Create and assign tasks with priorities and due dates.', category: 'Tasks', riskLevel: 'medium', requiresApproval: true, trustTier: 'first-party', readiness: 'available', permissions: ['read', 'write'] },
            // Notes
            { id: 'notes.create', name: 'Notes Create', description: 'Create and save structured notes with AI-generated summaries.', category: 'Notes', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read', 'write'] },
            { id: 'notes.search', name: 'Notes Search', description: 'Search through your notes library with semantic understanding.', category: 'Notes', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read'] },
            // Files
            { id: 'files.list', name: 'Files Browser', description: 'Browse and list local and connected cloud files.', category: 'Files', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read'] },
            { id: 'files.summarize', name: 'Files Summarize', description: 'Generate AI-powered summaries and insights from documents.', category: 'Files', riskLevel: 'low', requiresApproval: false, trustTier: 'first-party', readiness: 'available', permissions: ['read'] },
        ];
        setTools(fallbackTools);
        const g: Record<string, MCPTool[]> = {};
        fallbackTools.forEach(t => {
            if (!g[t.category]) g[t.category] = [];
            g[t.category].push(t);
        });
        setGrouped(g);
        setLoading(false);
    };

    const filteredCategories = Object.entries(grouped).filter(([category, catTools]) => {
        if (activeCategory && category !== activeCategory) return false;
        if (searchQuery) {
            return catTools.some(t =>
                t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                t.description.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        return true;
    });

    const openSetup = (tool: MCPTool) => {
        setSetupState({
            tool,
            step: 1,
            selectedScopes: tool.permissions.filter(p => p === 'read'),
            selectedReplicaId: replicas.length > 0 ? replicas[0].id : '',
            approvalPolicy: tool.requiresApproval ? 'ask-first' : 'auto-within-policy',
        });
    };

    const closeSetup = () => {
        setSetupState({ tool: null, step: 0, selectedScopes: [], selectedReplicaId: '', approvalPolicy: 'ask-first' });
        setConnectingToolId(null);
        setConnectError(null);
    };

    const toggleScope = (scope: string) => {
        setSetupState(prev => ({
            ...prev,
            selectedScopes: prev.selectedScopes.includes(scope)
                ? prev.selectedScopes.filter(s => s !== scope)
                : [...prev.selectedScopes, scope],
        }));
    };

    const handleConnect = async () => {
        if (!setupState.tool || !setupState.selectedReplicaId) {
            setConnectError('Please select a replica first.');
            return;
        }
        setConnectingToolId(setupState.tool.id);
        setConnectError(null);

        try {
            const response: any = await api.post('/mcp/catalog/connectors/setup', {
                toolId: setupState.tool.id,
                replicaId: setupState.selectedReplicaId,
                scopes: setupState.selectedScopes,
                approvalPolicy: setupState.approvalPolicy,
            });

            console.log('[Integrations] Setup response:', JSON.stringify(response, null, 2));

            // Check for API-level errors
            if (response.error) {
                console.error('[Integrations] Setup failed:', response.error, response.message);
                setConnectError(response.message || response.error);
                setConnectingToolId(null);
                return;
            }

            // If backend says this tool requires OAuth, redirect to auth URL
            const data = response.data || response;
            if (data?.requiresOAuth && data?.authorizationUrl) {
                console.log('[Integrations] Redirecting to OAuth:', data.authorizationUrl);
                window.location.href = data.authorizationUrl;
                return; // Page will navigate away
            }

            // Direct connection succeeded
            setConnectedTools(prev => new Set([...prev, setupState.tool!.id]));
            closeSetup();
        } catch (err: any) {
            console.error('[Integrations] Network error:', err);
            const detail = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Unknown error';
            setConnectError(`Connection failed: ${detail}`);
            setConnectingToolId(null);
        }
    };

    // ── Connector Setup Wizard ────────────────────────────────────────────────
    if (setupState.tool) {
        const tool = setupState.tool;
        const CategoryIcon = CATEGORY_ICONS[tool.category] || Plug;

        return (
            <div className="max-w-3xl mx-auto animate-fade-in">
                <div className="flex items-center gap-3 mb-8">
                    <button onClick={closeSetup} className="p-2 rounded-lg hover:bg-hig-bg-tertiary text-hig-text-secondary">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-hig-accent/20 flex items-center justify-center">
                        <CategoryIcon size={20} className="text-hig-accent" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-hig-text-primary">Connect {tool.name}</h2>
                        <p className="text-sm text-hig-text-secondary">{tool.description}</p>
                    </div>
                </div>

                {/* Progress */}
                <div className="flex items-center gap-2 mb-8">
                    {['Permissions', 'Assign to Replica', 'Confirm'].map((label, i) => (
                        <React.Fragment key={label}>
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${setupState.step >= i + 1 ? 'bg-hig-accent/10 text-hig-accent' : 'bg-hig-bg-tertiary text-hig-text-tertiary'}`}>
                                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px]">{i + 1}</span>
                                {label}
                            </div>
                            {i < 2 && <ChevronRight size={14} className="text-hig-text-tertiary" />}
                        </React.Fragment>
                    ))}
                </div>

                <div className="glass p-6">
                    {/* Step 1: Permissions */}
                    {setupState.step === 1 && (
                        <div className="space-y-6 animate-fade-in">
                            <div>
                                <h3 className="text-lg font-medium text-hig-text-primary mb-2">Permission Scopes</h3>
                                <p className="text-sm text-hig-text-secondary mb-4">
                                    Choose what your Replica can do. Default is <span className="text-emerald-400 font-medium">read-only</span> for safety.
                                </p>
                                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium mb-4 ${RISK_COLORS[tool.riskLevel]}`}>
                                    <Shield size={12} />
                                    {tool.riskLevel.charAt(0).toUpperCase() + tool.riskLevel.slice(1)} Risk
                                </div>
                                <div className="space-y-3">
                                    {tool.permissions.map(perm => (
                                        <label key={perm} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${setupState.selectedScopes.includes(perm) ? 'border-hig-accent/30 bg-hig-accent/5' : 'border-hig-border bg-hig-bg-tertiary hover:bg-hig-bg-elevated'
                                            }`}>
                                            <input type="checkbox" checked={setupState.selectedScopes.includes(perm)} onChange={() => toggleScope(perm)} className="rounded border-hig-border text-hig-accent focus:ring-hig-accent/20" />
                                            <div className="flex-1">
                                                <span className="text-sm font-medium text-hig-text-primary capitalize">{perm}</span>
                                                {perm === 'read' && <span className="ml-2 text-xs text-emerald-400">Safe default</span>}
                                                {perm === 'send' && <span className="ml-2 text-xs text-orange-400">Requires approval</span>}
                                                {perm === 'write' && <span className="ml-2 text-xs text-amber-400">Creates data</span>}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                {isEnterprise && (
                                    <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                        <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-300">Enterprise tools operate in the <strong>ENTERPRISE_DERIVATIVE</strong> memory layer and freeze when the contract ends.</p>
                                    </div>
                                )}
                            </div>
                            <button onClick={() => setSetupState(p => ({ ...p, step: 2 }))} className="w-full py-2.5 bg-hig-accent hover:opacity-88 text-[#0F172A] font-bold rounded-lg text-sm font-medium transition-colors">
                                Next: Assign to Replica
                            </button>
                        </div>
                    )}

                    {/* Step 2: Assign to Replica */}
                    {setupState.step === 2 && (
                        <div className="space-y-6 animate-fade-in">
                            <div>
                                <h3 className="text-lg font-medium text-hig-text-primary mb-2">Assign to Replica</h3>
                                <p className="text-sm text-hig-text-secondary mb-4">Choose which Replica will use this tool.</p>
                                <div className="space-y-2">
                                    {replicas.length > 0 ? replicas.map((r: SimpleReplica) => (
                                        <label key={r.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${setupState.selectedReplicaId === r.id ? 'border-hig-accent/30 bg-hig-accent/5' : 'border-hig-border bg-hig-bg-tertiary hover:bg-hig-bg-elevated'
                                            }`}>
                                            <input type="radio" name="replica" checked={setupState.selectedReplicaId === r.id} onChange={() => setSetupState(p => ({ ...p, selectedReplicaId: r.id }))} className="text-hig-accent" />
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                                                {r.name?.[0] || '?'}
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-hig-text-primary">{r.name || 'Unnamed Replica'}</span>
                                                <p className="text-xs text-hig-text-tertiary">{r.category || 'General'}</p>
                                            </div>
                                        </label>
                                    )) : (
                                        <div className="text-center py-8 text-hig-text-tertiary">
                                            <Plug size={24} className="mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">No replicas yet. Create one first.</p>
                                            <button onClick={() => onNavigate('create-replica')} className="mt-2 text-hig-accent text-sm hover:underline">Create Replica →</button>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-6">
                                    <label className="block text-xs font-medium text-hig-text-secondary mb-2 uppercase tracking-wide">Approval Policy</label>
                                    <select value={setupState.approvalPolicy} onChange={e => setSetupState(p => ({ ...p, approvalPolicy: e.target.value as any }))} className="w-full bg-hig-bg-tertiary border border-hig-border rounded-lg px-4 py-2.5 text-sm text-hig-text-primary focus:outline-none focus:ring-1 focus:ring-hig-accent">
                                        <option value="ask-first">Ask first (safest)</option>
                                        <option value="auto-within-policy">Auto within policy</option>
                                        <option value="never">Never execute automatically</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button onClick={() => setSetupState(p => ({ ...p, step: 1 }))} className="flex-1 py-2.5 bg-hig-bg-tertiary hover:bg-hig-bg-elevated text-hig-text-primary rounded-lg text-sm font-medium transition-colors">Back</button>
                                <button onClick={() => setSetupState(p => ({ ...p, step: 3 }))} disabled={!setupState.selectedReplicaId} className="flex-1 py-2.5 bg-hig-accent hover:opacity-88 text-[#0F172A] font-bold rounded-lg text-sm font-medium transition-colors disabled:opacity-50">Next: Confirm</button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Confirm */}
                    {setupState.step === 3 && (
                        <div className="space-y-6 animate-fade-in">
                            <h3 className="text-lg font-medium text-hig-text-primary mb-2">Confirm Connection</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center py-2 border-b border-hig-border">
                                    <span className="text-sm text-hig-text-secondary">Tool</span>
                                    <span className="text-sm font-medium text-hig-text-primary">{tool.name}</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-hig-border">
                                    <span className="text-sm text-hig-text-secondary">Permissions</span>
                                    <span className="text-sm font-medium text-hig-text-primary">{setupState.selectedScopes.join(', ')}</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-hig-border">
                                    <span className="text-sm text-hig-text-secondary">Replica</span>
                                    <span className="text-sm font-medium text-hig-text-primary">
                                        {replicas.find((r: SimpleReplica) => r.id === setupState.selectedReplicaId)?.name || '—'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-hig-border">
                                    <span className="text-sm text-hig-text-secondary">Approval</span>
                                    <span className="text-sm font-medium text-hig-text-primary capitalize">{setupState.approvalPolicy.replace(/-/g, ' ')}</span>
                                </div>
                                <div className="flex justify-between items-center py-2">
                                    <span className="text-sm text-hig-text-secondary">Memory Layer</span>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isEnterprise ? 'bg-purple-500/10 text-purple-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                                        {isEnterprise ? 'ENTERPRISE_DERIVATIVE' : 'CORE_PERSONAL'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-start gap-2 p-3 rounded-lg bg-hig-accent/10 border border-hig-accent/20">
                                <Shield size={16} className="text-hig-accent flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-hig-accent">All tool actions are logged to the immutable audit chain. The Decision Guardian will review high-risk actions.</p>
                            </div>
                            {connectError && (
                                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                                    <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-red-300">{connectError}</p>
                                </div>
                            )}
                            <div className="flex gap-3">
                                <button onClick={() => setSetupState(p => ({ ...p, step: 2 }))} className="flex-1 py-2.5 bg-hig-bg-tertiary hover:bg-hig-bg-elevated text-hig-text-primary rounded-lg text-sm font-medium transition-colors">Back</button>
                                <button onClick={handleConnect} disabled={connectingToolId === tool.id} className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                                    {connectingToolId === tool.id ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Connecting…
                                        </span>
                                    ) : (
                                        <span>✓ Connect</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Main Catalog View ─────────────────────────────────────────────────────
    return (
        <div className="max-w-6xl mx-auto animate-fade-in pb-12">
            {!hideHeader ? (
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-hig-text-primary">Integrations</h1>
                        <p className="text-sm text-hig-text-secondary mt-1">
                            {isEnterprise ? 'Connect enterprise tools to your licensed Replicas' : 'Connect tools your Replica can use to help you work'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-hig-text-tertiary">{tools.length} tools available</span>
                        <div className={`w-2 h-2 rounded-full ${tools.length > 0 ? 'bg-emerald-400' : 'bg-gray-400'}`} />
                    </div>
                </div>
            ) : (
                <div className="flex justify-end mb-4">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-hig-text-tertiary">{tools.length} tools available</span>
                        <div className={`w-2 h-2 rounded-full ${tools.length > 0 ? 'bg-emerald-400' : 'bg-gray-400'}`} />
                    </div>
                </div>
            )}

            {/* OAuth error banner */}
            {oauthCallbackError && (
                <div className="flex items-start gap-3 p-3 mb-6 rounded-lg bg-red-500/10 border border-red-500/20">
                    <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300 flex-1">{oauthCallbackError}</p>
                    <button onClick={() => setOauthCallbackError(null)} className="text-red-400 hover:text-red-300 text-xs">✕</button>
                </div>
            )}

            {/* Search + Filters */}
            <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary" />
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search tools..." className="w-full bg-hig-bg-tertiary border border-hig-border rounded-lg pl-9 pr-4 py-2.5 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent" />
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto">
                    <button onClick={() => setActiveCategory(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${!activeCategory ? 'bg-hig-accent/10 text-hig-accent border border-hig-accent/20' : 'bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary'}`}>
                        All
                    </button>
                    {Object.keys(grouped).map(cat => {
                        const Icon = CATEGORY_ICONS[cat] || Plug;
                        return (
                            <button key={cat} onClick={() => setActiveCategory(activeCategory === cat ? null : cat)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${activeCategory === cat ? 'bg-hig-accent/10 text-hig-accent border border-hig-accent/20' : 'bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary'}`}>
                                <Icon size={12} />
                                {cat}
                            </button>
                        );
                    })}
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center py-20">
                    <div className="w-6 h-6 border-2 border-hig-accent/30 border-t-hig-accent rounded-full animate-spin" />
                </div>
            )}

            {!loading && filteredCategories.map(([category, catTools]) => {
                const Icon = CATEGORY_ICONS[category] || Plug;
                const filtered = searchQuery
                    ? catTools.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()) || t.description.toLowerCase().includes(searchQuery.toLowerCase()))
                    : catTools;
                if (filtered.length === 0) return null;

                return (
                    <div key={category} className="mb-8">
                        <div className="flex items-center gap-2 mb-3">
                            <Icon size={16} className="text-hig-text-secondary" />
                            <h2 className="text-sm font-semibold text-hig-text-primary uppercase tracking-wider">{category}</h2>
                            <span className="text-xs text-hig-text-tertiary">({filtered.length})</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filtered.map(tool => {
                                const isConnected = connectedTools.has(tool.id);
                                return (
                                    <div key={tool.id} className="glass p-4 hover:bg-hig-bg-elevated transition-all group">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                {TOOL_LOGOS[tool.id] ? (
                                                    <div className={`w-8 h-8 rounded-lg ${TOOL_LOGOS[tool.id].bg} border border-hig-border flex items-center justify-center`}>
                                                        {TOOL_LOGOS[tool.id].svg}
                                                    </div>
                                                ) : (
                                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-hig-accent/10 flex items-center justify-center">
                                                        <Icon size={16} className="text-hig-accent" />
                                                    </div>
                                                )}
                                                <div>
                                                    <h3 className="text-sm font-medium text-hig-text-primary">{tool.name}</h3>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${TRUST_BADGES[tool.trustTier].color}`}>
                                                            {TRUST_BADGES[tool.trustTier].label}
                                                        </span>
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${RISK_COLORS[tool.riskLevel]}`}>
                                                            {tool.riskLevel}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <p className="text-xs text-hig-text-secondary mb-3 line-clamp-2">{tool.description}</p>
                                        <div className="flex items-center gap-1.5 mb-3">
                                            {tool.permissions.map(p => (
                                                <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-hig-bg-tertiary text-hig-text-tertiary capitalize">{p}</span>
                                            ))}
                                            {tool.requiresApproval && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 flex items-center gap-0.5">
                                                    <Shield size={8} /> Guardian
                                                </span>
                                            )}
                                        </div>
                                        <button onClick={() => !isConnected && openSetup(tool)} disabled={isConnected} className={`w-full py-2 rounded-lg text-xs font-medium transition-colors ${isConnected ? 'bg-emerald-500/10 text-emerald-400 cursor-default' : 'bg-hig-accent hover:opacity-88 text-[#0F172A] font-bold'}`}>
                                            {isConnected ? '✓ Connected' : 'Connect'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {!loading && filteredCategories.length === 0 && (
                <div className="text-center py-16 text-hig-text-tertiary">
                    <Search size={32} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No tools match your search</p>
                </div>
            )}
        </div>
    );
}
